import { patchSalesAction } from '@entities/sales-action/store.svelte';
import type { Contract } from '@domain/contract/model';
import type { SalesAction } from '@shared/types/sales-action';

/**
 * 契約作成 → 営業アクションへの `linked_contract_id` 紐付けフロー (86eya0dva PR4)。
 *
 * 契約作成とアクションへの紐付けはフロント 2 リクエスト設計 (バックエンドに専用の
 * 複合エンドポイントは無い)。中間状態 (契約はできたが未紐付け) を許容し、紐付けの
 * PATCH が失敗しても契約は削除せず、紐付けだけを再試行できるようにする。
 * firm/lawyer 両詳細ページから同じ状態を共有する (フロー判断は features に置く)。
 */

export interface ContractLinkRetry {
  action: SalesAction;
  contract: Contract;
}

interface ContractLinkState {
  sourceAction: SalesAction | null;
  retry: ContractLinkRetry | null;
  error: string | null;
}

const state = $state<ContractLinkState>({
  sourceAction: null,
  retry: null,
  error: null,
});

/// `isStale` は PATCH の非同期区間 (await) の間にページが別対象へ遷移していないかを
/// 呼び出し元 (ページ) に問い合わせる。 このモジュールは firm/lawyer 両ページで共有する
/// シングルトンなので、 遷移後に古い action/contract の結果で新しい対象の retry/error を
/// 汚さないよう、 stale なら状態更新も afterLink も行わず抜ける (旧 firm 側ローカル実装の
/// `if (firmId !== id) return` と同じ意味論)。
async function performLink(
  action: SalesAction,
  contract: Contract,
  afterLink?: () => void | Promise<void>,
  isStale?: () => boolean,
): Promise<void> {
  state.error = null;
  try {
    await patchSalesAction(action.id, { linked_contract_id: contract.id });
    if (isStale?.()) return;
    state.retry = null;
  } catch (err) {
    if (isStale?.()) return;
    state.retry = { action, contract };
    state.error = err instanceof Error ? err.message : String(err);
  }
  await afterLink?.();
}

export const contractLinkFlow = {
  /// 契約作成モーダルの作成元となった受注アクション (結果タブ/契約タブの CTA から
  /// 設定)。手動の「+ 契約を追加」では null のまま。
  get sourceAction(): SalesAction | null {
    return state.sourceAction;
  },
  /// 契約は作成できたが紐付け PATCH に失敗した場合の再試行対象。
  get retry(): ContractLinkRetry | null {
    return state.retry;
  },
  get error(): string | null {
    return state.error;
  },

  /// ページ遷移時などにまとめて破棄する (他 entity store の `clear()` と対称)。
  reset(): void {
    state.sourceAction = null;
    state.retry = null;
    state.error = null;
  },

  /// 結果タブ/契約タブの「契約を作成する」CTA から呼ぶ。作成元の受注アクションを保持する。
  startFrom(action: SalesAction): void {
    state.sourceAction = action;
  },

  /// 作成元アクションを持たない通常の「+ 契約を追加」を開始する時に呼ぶ。
  clearSource(): void {
    state.sourceAction = null;
  },

  /// 契約作成モーダルの `onCreated` から呼ぶ。作成元アクションが無ければ何もしない
  /// (手動の「+ 契約を追加」)。 あれば `linked_contract_id` の紐付けを試行し、
  /// 成否を問わず (stale でない限り) 呼び出し元の後処理 (一覧再取得等) を
  /// afterLink で行う。 `isStale` は非同期区間中にページが別対象へ遷移していないかの
  /// 問い合わせ (省略時は常に非 stale 扱い)。
  async handleCreated(
    contract: Contract,
    afterLink?: () => void | Promise<void>,
    isStale?: () => boolean,
  ): Promise<void> {
    const source = state.sourceAction;
    state.sourceAction = null;
    if (!source) return;
    await performLink(source, contract, afterLink, isStale);
  },

  /// 紐付け失敗後の再試行。契約は再作成せず、保持済みの action/contract で
  /// PATCH だけをやり直す。
  async retryLink(
    afterLink?: () => void | Promise<void>,
    isStale?: () => boolean,
  ): Promise<void> {
    const current = state.retry;
    if (!current) return;
    await performLink(current.action, current.contract, afterLink, isStale);
  },
};
